import { useNuxtApp } from '#app'
import { ref, computed, markRaw } from 'vue'
import { useRuntimeConfig } from '#app'
import type { Component } from 'vue'

interface ComponentInfo {
  name: string
  path: string
  relativePath?: string // For dev
  friendlyName?: string
  autoImportName?: string // For production
}

// Cache for component metadata (not imported, just info)
let componentRegistry: ComponentInfo[] | null = null

// Cache for actually loaded components
const loadedComponents = new Map<string, any>()

export const useBuilderComponents = (friendlyNames?: string[]) => {
  const config = useRuntimeConfig() as any
  const { prefix } = (config.public.builderIO.components as { prefix: string; dir: string; enabled: boolean })
  const isDev = process.env.NODE_ENV !== 'production'

  const components = ref<any[]>([])
  const isLoading = ref(false)
  let loadPromise: Promise<void> | null = null

  const loadComponents = async () => {
    if (isLoading.value) return loadPromise
    isLoading.value = true

    loadPromise = (async () => {
      try {
        // Handle production differently - use pre-generated loader
        if (!isDev) {
          try {
            // @ts-ignore
            const { loadBuilderComponents } = await import('#build/builder/component-loader.mjs')
            const loadedComponentsList = loadBuilderComponents(friendlyNames)
            
            if (!loadedComponentsList || loadedComponentsList.length === 0) {
              console.warn('No components loaded from component loader')
              components.value = []
              return
            }
            
            // Helpers to infer inputs from Vue props if metadata missing
            const isLikelyColor = (name: string, def: any) => {
              const n = (name || '').toLowerCase()
              if (n.includes('color') || n.includes('colour')) return true
              if (typeof def === 'string' && (def.startsWith('#') || def.startsWith('rgba(') || def.startsWith('hsl('))) return true
              return false
            }
            const isLikelyFile = (_name: string, def: any) => {
              if (typeof def === 'string' && /(\.(png|jpe?g|webp|svg|gif|mp4|webm|pdf)$)/i.test(def)) return true
              if (typeof def === 'string' && /^https?:\/\//i.test(def)) return true
              return false
            }
            const isLikelyRichText = (name: string) => {
              const n = (name || '').toLowerCase()
              return n.includes('richtext') || n.includes('html') || n.includes('content') || n.endsWith('texttopbold') || n.endsWith('texttopsmall')
            }
            const determineBuilderInputType = (name: string, opt: any) => {
              const type = opt?.type
              const def = opt?.default
              if (typeof opt?.builderType === 'string') return opt.builderType
              if (isLikelyColor(name, def)) return 'color'
              if (isLikelyFile(name, def)) return 'file'
              if (isLikelyRichText(name)) return 'richText'
              if (type === Boolean) return 'boolean'
              if (type === Number) return 'number'
              if (type === Array) return 'list'
              if (type === Object) return 'object'
              return 'text'
            }
            const extractInputsFromProps = (props: any) => {
              if (!props) return []
              if (Array.isArray(props)) return props.map((name: string) => ({ name, type: 'text' }))
              const inputs: any[] = []
              for (const [name, options] of Object.entries(props)) {
                const opt = typeof options === 'object' && options !== null ? options as any : { type: options }
                let defaultValue = opt.default
                if (typeof defaultValue === 'function') {
                  try {
                    // Only call zero-arg default factories (Vue style)
                    defaultValue = defaultValue.length === 0 ? defaultValue() : undefined
                  } catch {
                    defaultValue = undefined
                  }
                }
                // Ensure serializable
                if (typeof defaultValue === 'function' || typeof defaultValue === 'symbol') {
                  defaultValue = undefined
                }
                // Infer list subFields when default array reveals element shape
                let type = determineBuilderInputType(name, opt)
                const input: any = { name, type, defaultValue }
                if (type === 'list' && Array.isArray(defaultValue) && defaultValue.length > 0) {
                  const sample = defaultValue[0]
                  if (typeof sample === 'string') {
                    const elemType = isLikelyFile(name, sample) ? 'file' : 'text'
                    input.subFields = [{ name: 'value', type: elemType }]
                  } else if (sample && typeof sample === 'object' && !Array.isArray(sample)) {
                    input.subFields = Object.entries(sample as any).map(([k, v]) => {
                      let t = 'text'
                      if (typeof v === 'number') t = 'number'
                      else if (typeof v === 'boolean') t = 'boolean'
                      else if (typeof v === 'string') t = isLikelyFile(k, v) ? 'file' : (isLikelyColor(k, v) ? 'color' : (isLikelyRichText(k) ? 'richText' : 'text'))
                      return { name: k, type: t }
                    })
                  }
                }
                inputs.push(input)
              }
              return inputs
            }

            // Sanitize components to avoid making Vue components reactive and ensure inputs exist
            const sanitized = loadedComponentsList.map((c: any) => {
              const nonReactive = c?.component ? markRaw(c.component) : c.component
              const ensuredInputs = Array.isArray(c?.inputs) && c.inputs.length > 0
                ? c.inputs
                : extractInputsFromProps((c?.component as any)?.props)
              return {
                ...c,
                component: nonReactive,
                inputs: ensuredInputs,
              }
            })

            // Client-side debug logging for production
            try {
              // @ts-ignore
              if (typeof window !== 'undefined') {
                const summary = sanitized.map((c: any) => ({
                  name: c?.friendlyName || c?.name,
                  inputs: Array.isArray(c?.inputs)
                    ? c.inputs.map((i: any) => {
                        const base: any = { name: i?.name, type: i?.type }
                        if (i?.type === 'list') {
                          const len = Array.isArray(i?.defaultValue) ? i.defaultValue.length : 0
                          const sample = len > 0 ? i.defaultValue[0] : undefined
                          base.defaultLen = len
                          base.sampleItem = sample
                          base.subFields = i?.subFields
                        } else if (i?.defaultValue !== undefined) {
                          base.defaultValue = i.defaultValue
                        }
                        return base
                      })
                    : []
                }))
                // eslint-disable-next-line no-console
                console.log('[builder-components][client-debug] sanitized components', JSON.stringify(summary))
              }
            } catch {}

            // Add to cache
            for (const component of sanitized) {
              const cacheKey = component.friendlyName || component.name
              loadedComponents.set(cacheKey, component)
            }

            components.value = sanitized
            //console.log(`Loaded ${loadedComponentsList.length} components in production`)
            return
          } catch (error) {
            console.error('Failed to load component loader:', error)
            components.value = []
            return
          }
        }

        // Development mode - use dynamic discovery
        // Get component registry from API if we don't have it
        if (!componentRegistry) {
          try {
            // @ts-ignore - $fetch is available at runtime
            componentRegistry = await globalThis.$fetch('/api/builder-components')
            //console.log('Discovered components:', componentRegistry?.length || 0)
          } catch (error) {
            console.error('Failed to fetch component registry:', error)
            componentRegistry = []
          }
        }

        if (!componentRegistry || componentRegistry.length === 0) {
          console.warn('No components found in registry')
          components.value = []
          return
        }

        // Determine which components to load
        let componentsToLoad: ComponentInfo[] = []
        
        if (friendlyNames && friendlyNames.length > 0 && componentRegistry) {
          // Try to match requested names with component friendly names
          componentsToLoad = friendlyNames.map(requestedName => {
            // Find all components that match the requested name
            const candidates = componentRegistry!.filter(c => {
              const friendlyNameMatch = c.friendlyName === requestedName ||
                c.friendlyName?.toLowerCase() === requestedName.toLowerCase()
              return friendlyNameMatch
            })
            
            if (candidates.length === 0) {
              // Try partial match if no exact match
              const partialMatch = componentRegistry!.find(c => 
                c.friendlyName?.toLowerCase().includes(requestedName.toLowerCase()) ||
                requestedName.toLowerCase().includes(c.friendlyName?.toLowerCase() || '')
              )
              if (partialMatch) {
                return partialMatch
              }
              console.warn(`Component "${requestedName}" not found in registry`)
              return null
            } else if (candidates.length === 1) {
              // Only one match, use it
              return candidates[0]
            } else {
              // Multiple matches, try to pick the best one
              // Prefer components from certain directories based on common patterns
              const preferredDirs = ['healthyCoffee', 'healthyCoffee1', 'healtyCoffee22Builder']
              
              for (const dir of preferredDirs) {
                const match = candidates.find(c => c.relativePath && c.relativePath.includes(dir))
                if (match) {
                  console.log(`Multiple components named "${requestedName}" found, using ${match.relativePath}`)
                  return match
                }
              }
              
              // If no preferred match, just use the first one
              console.log(`Multiple components named "${requestedName}" found, using ${candidates[0].relativePath || candidates[0].path}`)
              return candidates[0]
            }
          }).filter(Boolean) as ComponentInfo[]
        } else {
          // Load all components
          componentsToLoad = componentRegistry || []
        }

        //console.log(`Loading ${componentsToLoad.length} components:`, componentsToLoad.map(c => c.friendlyName))

        // Load only the requested components
        const loadedComponentsList = []
        
        for (const componentInfo of componentsToLoad) {
          const cacheKey = componentInfo.friendlyName || componentInfo.name
          
          // Check if already loaded
          if (loadedComponents.has(cacheKey)) {
            loadedComponentsList.push(loadedComponents.get(cacheKey))
            continue
          }
          
          try {
            if (isDev) {
              // In development, use dynamic imports with raw paths
              const componentPath = componentInfo.path
              const nuxtUrl = `/_nuxt${componentPath}?builder=true`
              const componentUrl = `/_nuxt${componentPath}`
              
              // First, import the actual Vue component
              const componentModule = await import(/* @vite-ignore */ componentUrl)
              const vueComponent = componentModule.default
              
              // Then, import the builder metadata
              const builderDataModule = await import(/* @vite-ignore */ nuxtUrl)
              
              if (builderDataModule.default && !builderDataModule.default.ignored) {
                // Create the proper component structure with the actual Vue component
                const component = {
                  friendlyName: builderDataModule.default.friendlyName,
                  name: builderDataModule.default.friendlyName, // Use friendlyName as name
                  component: markRaw(vueComponent), // Use the actual Vue component, and keep it non-reactive
                  inputs: builderDataModule.default.inputs || [],
                  canHaveChildren: builderDataModule.default.canHaveChildren !== false,
                  // Include any other properties from builder metadata if they exist
                  ...(builderDataModule.default.description && { description: builderDataModule.default.description }),
                  ...(builderDataModule.default.schema && { schema: builderDataModule.default.schema })
                }
                
                // Cache it
                loadedComponents.set(cacheKey, component)
                loadedComponentsList.push(component)
              }
            }
          } catch (error) {
            console.warn(`Failed to load component ${componentInfo.name}:`, error)
          }
        }

        components.value = loadedComponentsList
      } catch (error) {
        console.error('Failed to load builder components:', error)
      } finally {
        isLoading.value = false
      }
    })()

    return loadPromise
  }

  // Start loading immediately
  const initialLoadPromise = loadComponents()

  // Return both the computed ref and a way to await loading
  return {
    components: computed(() => components.value),
    ready: initialLoadPromise
  }
}
